import { DeleteOutline, Edit } from '@mui/icons-material';
import { IconButton, Stack, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import Alert from '/@/components/bayer/alert';
import ChipLabel from '/@/components/chip-label';
import { useGetUserNode } from '/@/hooks/nodes';
import { useIsValidatedAdvisor, useIsValidatedRV } from '/@/hooks/session';
import { _ } from '/@/locale';
import { formatCuitDisplay } from '../add-grower-section/cuit-text-field';
import type { SidebarGrower } from '../index';

const GrowerCard = ({
    grower,
    index,
    onEdit,
    onDelete,
}: {
    grower: SidebarGrower;
    index: number;
    onEdit: (index: number) => void;
    onDelete: (index: number) => void;
}) => {
    const isRV = useIsValidatedRV();
    const isAdvisor = useIsValidatedAdvisor();

    const rvNodeQuery = useGetUserNode(Number(grower?.rv) || 0);
    const advisorNodeQuery = useGetUserNode(Number(grower?.advisor) || 0);

    if (grower.editable)
        return (
            <Stack className='mp-mask' sx={{ background: 'white', textAlign: 'start' }} p={2} spacing={2}>
                <Stack direction='row' justifyContent='space-between' alignItems='center'>
                    <Typography variant='caption' color='primary' fontWeight='bold'>
                        {grower.name ?? formatCuitDisplay(grower.cuit)}
                    </Typography>
                    <Stack direction='row'>
                        <IconButton
                            size='small'
                            color='secondary'
                            onClick={() => onEdit(index)}
                            aria-label={_('Editar')}
                            sx={{
                                '& .MuiSvgIcon-root': {
                                    fontSize: '16px',
                                    width: '16px',
                                    height: '16px',
                                },
                            }}
                        >
                            <Edit />
                        </IconButton>
                        <IconButton
                            size='small'
                            color='secondary'
                            onClick={() => onDelete(index)}
                            aria-label={_('Eliminar')}
                            sx={{
                                '& .MuiSvgIcon-root': {
                                    fontSize: '16px',
                                    width: '16px',
                                    height: '16px',
                                },
                            }}
                        >
                            <DeleteOutline />
                        </IconButton>
                    </Stack>
                </Stack>
                <Typography
                    variant='caption'
                    color='primary.mui.inactive'
                    sx={{
                        alignSelf: 'start',
                    }}
                >
                    {`CUIT ${formatCuitDisplay(grower.cuit)}`}
                </Typography>
                {isAdvisor && (
                    <ChipLabel
                        label={grower.segment === 'premium' ? _('Premium') : _('Otros segmentos')}
                        type={grower.segment === 'premium' ? 'premiumGrower' : 'commonGrower'}
                    />
                )}
                {isRV && advisorNodeQuery.data?.name && (
                    <ChipLabel label={advisorNodeQuery.data?.name} type='advisor' />
                )}
            </Stack>
        );

    return (
        <Stack
            className='mp-mask'
            sx={{
                background: (theme: Theme) => theme.palette.primary.infoBackground,
                textAlign: 'start',
            }}
            p={2}
            spacing={1}
        >
            <Stack>
                <Typography variant='caption' color='primary'>
                    CUIT {formatCuitDisplay(grower.cuit)}
                </Typography>
                <Typography variant='caption' color='primary' fontWeight='bold'>
                    {`${_('Productor asignado a')} ${grower.rv ? rvNodeQuery.data?.name : advisorNodeQuery.data?.name}`}
                </Typography>
            </Stack>
            {rvNodeQuery.data?.name && <ChipLabel label={rvNodeQuery.data?.name} type='rv' />}
            {advisorNodeQuery.data?.name && <ChipLabel label={advisorNodeQuery.data?.name} type='advisor' />}
            <Typography variant='caption' color='success.main'>
                {_('Solicitud enviada')}
            </Typography>
        </Stack>
    );
};
const NewGrowersModalSidebar = ({
    growers,
    onEdit,
    onDelete,
}: {
    growers: SidebarGrower[];
    onEdit: (index: number) => void;
    onDelete: (index: number) => void;
}) => {
    return (
        <Stack
            sx={{
                background: (theme: Theme) => theme.palette.grey[100],
                height: '100%',
            }}
            p={3}
            spacing={3}
        >
            <Alert
                severity='surface'
                sx={{
                    textAlign: 'start',
                }}
            >
                {_('Aquí se mostrarán los productores que agregues')}
            </Alert>
            {growers.map((grower, index) => (
                <GrowerCard
                    key={`${grower.cuit}-${index}`}
                    grower={grower}
                    index={index}
                    onEdit={onEdit}
                    onDelete={onDelete}
                />
            ))}
        </Stack>
    );
};

export default NewGrowersModalSidebar;
